/**
 * Who gets the tenant / sub-tenant switcher in the header, and what it offers.
 *
 * The trap this exists to avoid: a platform operator inside a proxy session is
 * a shadow `workspace_admin` of the tenant they entered, so `user.isSuperadmin`
 * — which is just `role === 'superadmin'` — is FALSE for the whole session.
 * Deriving the operator console from the role therefore hid the switcher the
 * moment it was first used, and `/tenants/my-locations` answers `can_switch:
 * false` inside an operator proxy by design, so nothing replaced it. The
 * operator was stranded in the first tenant they opened.
 *
 * The server's `scope` on `/tenants/proxy-targets` is resolved from the *source*
 * user (see `_resolve_proxy_source_context`), so it survives the hop. It, not
 * the role, is the signal.
 */

/** Whether to fetch proxy targets at all. */
export const shouldLoadProxyTargets = (user) => {
  if (user?.isSuperadmin) return true;
  // Inside a proxy session the role and the parent tenant both belong to the
  // target, so neither can be used to bail out here. Fetch, then let `scope`
  // decide what to render.
  if (user?.isProxySession) return true;
  if (user?.parentTenantId || user?.parentTenantSlug) return false;
  return ['workspace_admin', 'hr_admin'].includes(String(user?.role || '').toLowerCase());
};

/** Whether this header belongs to a platform operator. */
export const isOperatorConsole = (user, scope) =>
  Boolean(user?.isSuperadmin || scope === 'superadmin');

/**
 * Sub-tenants on offer. With no parent picked this is every sub-tenant, not
 * none: reaching a branch should not require proxying into its head office
 * first, and returning `[]` left the select permanently empty on the operator's
 * own dashboard — where a parent is never selected.
 */
export const subTenantOptions = (items = [], selectedParent = null) =>
  (selectedParent
    ? items.filter((row) => Number(row.parent_tenant_id) === Number(selectedParent.id))
    : items.filter((row) => row.parent_tenant_id));
